<template>
  <span v-if="EXPRESSION_TYPES.TRUE === expressionType">Default</span>

  <span v-else-if="EXPRESSION_TYPES.OPERATOR_AVAILABLE === expressionType" class="flex items-center">
    <operator-online-icon v-if="expression[expressionType]" class="h-6 w-6" :class="{'text-success': !isSelected && !isHover}"/>
    <operator-offline-icon v-else class="h-6 w-6" :class="{'text-focus-error': !isSelected && !isHover}"/>
    <span v-if="showText" class="ml-1">
      {{ `Operators are ${expression[expressionType] ? 'online' : 'offline'}` }}
    </span>
  </span>

  <span v-else-if="EXPRESSION_TYPES.DAYS_OF_WEEK === expressionType">
   {{ resolveDaysOfWeekLabel() }}
  </span>

  <span v-else-if="EXPRESSION_TYPES.TIME_BETWEEN === expressionType" class="flex items-center">
    <vs-icon icon="schedule" size="1.2rem" :class="isSelected || isHover ? 'white' : 'text-gray'"/>
    <span class="ml-1">
      {{ resolveTimeBetweenLabel() }}
    </span>
  </span>

  <span v-else-if="isTypeContextChatGPTCallOperator" class="flex justify-center">
    <span class="truncate">AI Call Operator</span>
  </span>

  <span v-else-if="[EXPRESSION_TYPES.KEY_EQUALS, EXPRESSION_TYPES.KEY_REGEX].includes(expressionType) && INPUT_TYPES.CONTEXT === inputType" class="flex justify-center">
    <span class="truncate">{{ resolveContextLabel() }}</span>
  </span>

  <span v-else-if="EXPRESSION_TYPES.KEY_REGEX === expressionType" class="flex items-center justify-center">
    <span class="mr-1 mt-1 h-full">
      <url-icon class="h-4"/>
    </span>
    <span class="truncate" v-html="browserUrlLabel" :title="browserUrlLabel"/>
  </span>

  <div v-else-if="EXPRESSION_TYPES.ENUM === expressionType" class="inline-flex justify-center ">
    <span class="mr-1 pt-0.5">Channel:</span>
    <template v-if="expression[expressionType].length">
      <component v-for="(channelType, i) of expression[expressionType]"
                 :is="CHANNEL_ICONS[channelType]"
                 :key="i"
                 class="inline-block h-4 w-4 my-auto ml-1"
      />
    </template>
    <span v-else class="pt-0.5">None</span>
  </div>

  <span v-else>Condition</span>
</template>

<script>
import { EXPRESSION_TYPES, INPUT_TYPES } from '../../../utils/bot-json.constants'
import AmioChatIcon from '@/assets/images/channels/AmioChatIcon'
import MessengerIcon from '@/assets/images/channels/MessengerIcon'
import WhatsappIcon from '@/assets/images/channels/WhatsappIcon'
import OperatorOnlineIcon from '@/assets/images/icons/OperatorOnlineIcon'
import OperatorOfflineIcon from '@/assets/images/icons/OperatorOfflineIcon'
import isEmpty from 'ramda/src/isEmpty'
import last from 'ramda/src/last'
import keys from 'ramda/src/keys'
import UrlIcon from '@/assets/images/icons/UrlIcon.vue'
import conditionUtils from '@/utils/condition.utils'
import keyRegexUtils from '@/utils/key-regex.utils'
import variableRefsUtils from '@/utils/variable-refs.utils'
import botModule from '@/store/modules/bot.module'

const CHANNEL_ICONS = {
  facebook_messenger: 'messenger-icon',
  amio_chat: 'amio-chat-icon',
  whatsapp: 'whatsapp-icon'
}

export default {
  name: 'ConditionCaseLabel',
  components: {
    UrlIcon,
    AmioChatIcon,
    MessengerIcon,
    WhatsappIcon,
    OperatorOnlineIcon,
    OperatorOfflineIcon
  },

  props: {
    condition: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isHover: {
      type: Boolean,
      default: false
    },
    showText: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      EXPRESSION_TYPES,
      INPUT_TYPES,
      CHANNEL_ICONS
    }
  },

  computed: {
    inputType() {
      return this.condition.input
    },

    expression() {
      return this.condition.expression
    },

    expressionType() {
      return keys(this.expression)[0] // it's guaranteed expression has only one key
    },

    browserUrlLabel() {
      const { regex } = this.expression[this.expressionType]
      return keyRegexUtils.buildBrowserUrlLabel(regex)
    },

    isTypeContextChatGPTCallOperator() {
      return this.expressionType === EXPRESSION_TYPES.KEY_EQUALS && this.inputType === INPUT_TYPES.CONTEXT &&
        this.expression.keyEquals?.key === 'chatgpt.callOperator' && this.expression.keyEquals?.equals === 'yes' && botModule.isEmailBot()
    }
  },

  methods: {
    removeLeadingZero(timeString) {
      return (timeString.substring(0, 2) === '00') ? timeString : timeString.replace(/^0+/, '')
    },

    resolveDaysOfWeekLabel() {
      const daysOfWeek = this.expression[this.expressionType]
      if (isEmpty(daysOfWeek)) return 'Day: None'

      const formatDaysOfWeek = (length) => daysOfWeek.map(day => day[0].toUpperCase() + day.substring(1, length)).join(', ')
      if (daysOfWeek.length > 5) return formatDaysOfWeek(2)

      if (daysOfWeek.length === 5 && !daysOfWeek.includes('saturday') && !daysOfWeek.includes('sunday')) {
        return 'Weekdays'
      }

      return daysOfWeek.length === 2 && daysOfWeek[0] === 'saturday' && daysOfWeek[1] === 'sunday' ?
        'Weekend' :
        formatDaysOfWeek(3)
    },

    resolveTimeBetweenLabel() {
      const from = variableRefsUtils.substituteVariableRefs(this.expression[this.expressionType].from)
      const to = variableRefsUtils.substituteVariableRefs(this.expression[this.expressionType].to)
      return `${this.removeLeadingZero(from)}-${this.removeLeadingZero(to)}`
    },

    resolveContextLabel() {
      const expressionValue = this.expression[this.expressionType]
      const payloadKey = conditionUtils.getContextPayloadKey(this.expressionType)
      return '${} ' + last(expressionValue.key.split('.')) + ' = ' + variableRefsUtils.substituteVariableRefs(expressionValue[payloadKey])
    }
  }
}
</script>
